import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { Mail, ArrowLeft, Loader2, Sun, Moon, CheckCircle, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';


import { translateAuthError } from '../../utils/authErrors';

const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function ForgotPassword() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const { theme, toggleTheme } = useTheme();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess(false);

        try {
            const trimmedEmail = email.trim();
            const { error } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
                redirectTo: window.location.origin + '/reset-password',
            });

            if (error) {
                console.error('Erreur de réinitialisation:', error);
                throw error;
            }
            setSuccess(true);
        } catch (err: any) {
            console.error('Catch error:', err);
            setError(translateAuthError(err.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-dark-bg transition-colors duration-300 flex items-center justify-center px-4 py-12 relative">
            <SEO
                title={t('auth.forgot.title')}
                description={t('auth.forgot.subtitle')}
                canonical="/forgot-password"
            />
            <div className="absolute top-4 right-4">

                <button
                    onClick={toggleTheme}
                    className="p-2.5 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
                    title={theme === 'dark' ? t('dashboard.sidebar.mode_light') || 'Mode clair' : t('dashboard.sidebar.mode_dark') || 'Mode sombre'}
                >
                    {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                </button>
            </div>

            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 shrink-0">
                            <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
                        </div>
                        <span className="text-3xl font-bold text-text-main">CitoyenPro</span>
                    </Link>
                    <h1 className="text-3xl font-bold text-text-main mb-2">{t('auth.forgot.title')}</h1>
                    <p className="text-text-muted">{t('auth.forgot.subtitle')}</p>
                </div>

                {/* Form Container */}
                <div className="bg-dark-card rounded-2xl border border-dark-border p-8 shadow-xl">
                    {error && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                            <p className="text-sm text-red-500 flex items-center gap-2">
                                <AlertTriangle size={16} />
                                {error}
                            </p>
                        </div>
                    )}

                    {success ? (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="text-green-500" size={32} />
                            </div>
                            <h2 className="text-xl font-bold text-text-main mb-2">{t('auth.forgot.success')}</h2>
                            <p className="text-text-muted mb-4">
                                {t('auth.forgot.success')}
                            </p>
                            <Link
                                to="/login"
                                className="w-full py-3 bg-dark-bg border border-dark-border text-text-main font-bold rounded-xl hover:bg-dark-hover transition-all inline-block"
                            >
                                {t('auth.forgot.back_login')}
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="email" className="block text-sm font-semibold text-text-main mb-2">
                                    {t('auth.login.email')}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Mail className="text-text-muted" size={20} />
                                    </div>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-colors text-sm text-text-main placeholder-text-muted"
                                        placeholder={t('auth.login.email_placeholder')}
                                        required
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="animate-spin" size={20} />
                                        {t('auth.forgot.sending')}
                                    </>
                                ) : (
                                    t('auth.forgot.submit')
                                )}
                            </button>
                        </form>
                    )}

                    {!success && (
                        <div className="mt-6 text-center">
                            <Link to="/login" className="text-sm text-text-main hover:text-primary font-semibold transition-colors flex items-center justify-center gap-2">
                                <ArrowLeft size={16} />
                                {t('auth.forgot.back_login')}
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
